import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Users, Calendar, DollarSign, AlertTriangle, Trophy, Image as ImageIcon, FileText, Home, MessageSquare } from "lucide-react";
import ResetTestDataButton from "@/components/admin/ResetTestDataButton";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-white/40 font-mono">Loading dashboard…</div>;

  return (
    <div data-testid="admin-dashboard">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Operations Overview</div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">
          Command <span className="brand-orange">center</span>.
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <ResetTestDataButton />
          <a
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/user-guide/pdf`}
            target="_blank" rel="noreferrer"
            data-testid="admin-guide-pdf-btn"
            className="border border-brand-orange text-brand-orange px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black flex items-center gap-2"
          >
            📘 Download Admin Guide (PDF)
          </a>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Parents" value={data.total_parents} testId="stat-parents" />
        <Stat icon={Trophy} label="Wards" value={data.total_wards} testId="stat-wards" />
        <Stat icon={Calendar} label="Bookings (all)" value={data.total_bookings} testId="stat-bookings-total" />
        <Stat icon={Calendar} label="Bookings (month)" value={data.month_bookings} tone="cyan" testId="stat-bookings-month" />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat icon={DollarSign} label="Invoice Revenue" value={formatINR(data.invoice_revenue)} tone="lime" testId="stat-invoice-rev" />
        <Stat icon={DollarSign} label="Booking Revenue" value={formatINR(data.booking_revenue)} tone="lime" testId="stat-booking-rev" />
        <Stat icon={AlertTriangle} label="Overdue Invoices" value={data.overdue_invoices} tone="red" testId="stat-overdue" />
      </div>

      <div className="mt-10 border border-white/12 bg-[#131A2B] p-6">
        <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Bookings by Service</div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_service}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="service" stroke="#71717a" fontSize={10} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid rgba(255,255,255,0.1)" }} />
              <Bar dataKey="count" fill="#39FF14" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-10">
        <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Website & Media shortcuts</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Shortcut to="/admin/gallery" icon={ImageIcon} label="Gallery" sub="Photos + Videos" testId="shortcut-gallery" />
          <Shortcut to="/admin/homepage" icon={Home} label="Homepage" sub="Hero + Contact" testId="shortcut-homepage" />
          <Shortcut to="/admin/blogs" icon={FileText} label="Blogs" sub="Journal posts" testId="shortcut-blogs" />
          <Shortcut to="/admin/testimonials" icon={MessageSquare} label="Testimonials" sub="Quotes" testId="shortcut-testimonials" />
        </div>
      </div>
    </div>
  );
}

function Shortcut({ to, icon: Icon, label, sub, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="border border-white/12 bg-[#131A2B] p-4 flex items-center gap-3 hover:border-brand-blue hover:bg-white/5 transition group"
    >
      <div className="w-10 h-10 flex items-center justify-center bg-brand-blue/10 brand-blue group-hover:bg-brand-blue group-hover:text-white transition">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold truncate">{label}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 truncate">{sub}</div>
      </div>
    </Link>
  );
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  const colorClass = tone === "cyan" ? "brand-orange" : tone === "red" ? "text-red-400" : "brand-blue";
  return (
    <div data-testid={testId} className="border border-white/12 bg-[#131A2B] p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon size={16} className={colorClass} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</span>
      </div>
      <div className={`font-display text-3xl font-black ${colorClass}`}>{value}</div>
    </div>
  );
}
